import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './magasin.css';
import { MagasinInfo } from '../../../Modeles/MagasinModel';
import AfficheMagasinsService from '../../../Services/Magasin/AfficheMagasinsService';
import { NewSideBarAdmin } from '../../NewSideBarAdmin';

export interface ProductType {
  product: MagasinInfo[];
  messageErrors: string;
}

export default function MagasinUpgraded() {
  const [state, setState] = useState<ProductType>({
    product: [],
    messageErrors: 'Aucun produit',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    AfficheMagasinsService()
      .getMagasin()
      .then((res) => {
        setState({ ...state, product: res.data });
        setLoading(false);
      })
      .catch((msg) => {
        setState({ ...state, messageErrors: msg.messageErrors });
        setLoading(false);
      });
  }, []);

  const { product } = state;

  const filteredProducts = product.filter((pro) =>
    pro.NomMagasin.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handleChangePage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  /* ─────────────────────────────────────
     MOBILE VIEW
  ───────────────────────────────────── */
  if (screenWidth <= 450) {
    return (
      <>
        <NewSideBarAdmin />
        <div className="mobile-container">

          {/* Mobile Search */}
          <div className="mobile-search-container">
            <div className="mobile-search-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mobile-search-input"
                type="text"
                placeholder="Rechercher un magasin..."
                value={searchTerm}
              />
              {searchTerm && (
                <i
                  className="bi bi-x-circle clear-icon"
                  onClick={() => setSearchTerm('')}
                ></i>
              )}
            </div>
            <Link to="/magasin/add" className="mobile-add-btn">
              <i className="bi bi-plus-lg"></i>
            </Link>
          </div>

          {/* Mobile Quick Actions */}
          <div className="mobile-quick-actions">
            <Link to="/commission" className="mobile-quick-btn btn-commission-page">
              <i className="bi bi-percent"></i>
              <span>Commission</span>
            </Link>
            <Link to="/demandes" className="mobile-quick-btn btn-demandes-page">
              <i className="bi bi-inbox-fill"></i>
              <span>Demandes</span>
            </Link>
          </div>

          {/* Mobile Content */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <span className="loading-text">Chargement...</span>
            </div>
          ) : currentItems.length > 0 ? (
            <div className="mobile-cards-container">
              {currentItems.map((pro: any) => (
                <div key={pro.IdMagasin} className="mobile-card">
                  <div className="mobile-card-header">
                    <div className="mobile-card-title-section">
                      <h3 className="mobile-card-title">{pro.NomMagasin}</h3>
                      <div className="mobile-card-rating">
                        <i className="bi bi-star-fill"></i>
                        <span>4.8</span>
                        <span className="review-count">(30K)</span>
                      </div>
                    </div>
                    <div className="mobile-card-icon">
                      <i className="bi bi-shop-window"></i>
                    </div>
                  </div>

                  <div className="mobile-card-body">
                    <div className="mobile-info-item">
                      <i className="bi bi-boxes info-icon"></i>
                      <span>{pro.NomMagasin === 'PAGEC' ? '+40k articles' : '+900 articles'}</span>
                    </div>
                    <div className="mobile-info-item">
                      <i className="bi bi-geo-alt info-icon"></i>
                      <span>{pro.adresse_de_siege}</span>
                    </div>
                  </div>

                  <Link to={`/magasin/${pro.IdMagasin}/edit`} className="mobile-card-link">
                    Plus de détails
                    <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="mobile-empty-state">
              <i className="bi bi-shop empty-icon"></i>
              <p className="empty-text">Aucun magasin trouvé</p>
              <p className="empty-subtext">Essayez de modifier votre recherche</p>
            </div>
          )}

          {/* Mobile Pagination */}
          {totalPages > 1 && (
            <div className="mobile-pagination">
              <button
                className="pagination-btn"
                onClick={() => handleChangePage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <span className="pagination-info">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handleChangePage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  /* ─────────────────────────────────────
     DESKTOP VIEW
  ───────────────────────────────────── */
  return (
    <>
      <NewSideBarAdmin />
      <div className="desktop-container">

        {/* Header */}
        <div className="header-section">
          <div className="header-content">
            <div className="header-left">
              <h1 className="page-title">Magasins</h1>
              <p className="page-subtitle">
                Gérez vos magasins et leurs informations
              </p>
            </div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="search-filter-bar">
          <div className="search-section">
            <div className="search-input-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                placeholder="Rechercher un magasin par nom..."
                value={searchTerm}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              )}
            </div>
          </div>

          <div className="action-section">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Vue grille"
              >
                <i className="bi bi-grid-3x3-gap"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Vue liste"
              >
                <i className="bi bi-list-ul"></i>
              </button>
            </div>

            {/* ── NEW BUTTONS ── */}
            <Link to="/commission" className="add-magasin-btn btn-commission-page">
              <i className="bi bi-percent"></i>
              Commission
            </Link>
            <Link to="/demandes" className="add-magasin-btn btn-demandes-page">
              <i className="bi bi-inbox-fill"></i>
              Demandes
            </Link>
            {/* ── END NEW BUTTONS ── */}

            <Link to="/magasin/add" className="add-magasin-btn">
              <i className="bi bi-plus-circle"></i>
              Ajouter un magasin
            </Link>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <span className="loading-text">Chargement des magasins...</span>
          </div>
        ) : currentItems.length > 0 ? (
          <>
            <div className={`magasins-container ${viewMode}-view`}>
              {currentItems.map((pro: any) => (
                <div key={pro.IdMagasin} className="magasin-card">

                  <div className="card-header">
                    <div className="card-logo-section">
                      <i className="bi bi-shop-window"></i>
                    </div>
                    <div className="card-badge">
                      <i className="bi bi-patch-check-fill"></i>
                      Vérifié
                    </div>
                  </div>

                  <div className="card-body">
                    <h3 className="card-title">{pro.NomMagasin}</h3>
                    <div className="card-stats">
                      <div className="stat-item">
                        <i className="bi bi-boxes"></i>
                        <span>{pro.NomMagasin === 'PAGEC' ? '+40k' : '+900'} articles</span>
                      </div>
                      <div className="stat-divider"></div>
                      <div className="stat-item rating">
                        <i className="bi bi-star-fill"></i>
                        <span>4.8</span>
                        <span className="reviews">(30K avis)</span>
                      </div>
                    </div>
                    <div className="card-location">
                      <i className="bi bi-geo-alt-fill"></i>
                      <p>{pro.adresse_de_siege}</p>
                    </div>
                  </div>

                  <div className="card-footer">
                    <Link to={`/magasin/${pro.IdMagasin}/edit`} className="view-details-btn">
                      Voir les détails
                      <i className="bi bi-arrow-right"></i>
                    </Link>
                  </div>

                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button
                  className="pagination-nav-btn"
                  onClick={() => handleChangePage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i>
                  Précédent
                </button>

                <div className="pagination-numbers">
                  {getPageNumbers().map((page, index) =>
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handleChangePage(page as number)}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <button
                  className="pagination-nav-btn"
                  onClick={() => handleChangePage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon-wrapper">
              <i className="bi bi-shop"></i>
            </div>
            <h3 className="empty-title">Aucun magasin trouvé</h3>
            <p className="empty-description">
              {searchTerm
                ? `Aucun magasin ne correspond à "${searchTerm}"`
                : 'Commencez par ajouter votre premier magasin'}
            </p>
            {searchTerm && (
              <button
                className="clear-filters-btn"
                onClick={() => setSearchTerm('')}
              >
                <i className="bi bi-x-circle"></i>
                Effacer la recherche
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}